<template>
  <section class="figaro-video">
    <div class="container">
      <div class="text-block">
        <h3>{{ $t('video-title') }}</h3>
        <p class="desc">{{ $t('video-desc') }}</p>
        <p class="figaro-link">
          <a href="https://www.lefigaro.fr/economie/aas-architecture-en-suisse-le-choix-d-une-architecture-sensible-et-maitrisee-20260224" target="_blank" rel="noopener">
            <strong>{{ $t('video-link') }}</strong>
            <svg fill="none" height="512" viewBox="0 0 24 24" width="512" xmlns="http://www.w3.org/2000/svg" id="fi_7491055"><g fill="rgb(0,0,0)"><path d="m18.75 21.75h-16.5v-16.875h9v-1.5h-10.5v19.875h19.5v-10.5h-1.5z"></path><path d="m14.25.75v1.5h6.4394l-11.84467 11.8447 1.06059 1.0606 11.84468-11.84466v6.43936h1.5v-9z"></path></g>
            </svg>
          </a>
        </p>
      </div>
      <div
        class="video-wrapper"
        :class="{ playing: isPlaying }"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @click="togglePlay"
      >
        <video
          ref="video"
          poster="/static/video-miniature.jpg"
          preload="metadata"
          @play="isPlaying = true"
          @pause="isPlaying = false"
          @ended="isPlaying = false; progress = 0"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onLoadedMetadata"
        >
          <source src="/static/video.mp4" type="video/mp4" />
        </video>

        <!-- Big play button when paused -->
        <transition name="fade">
          <button class="play-btn" v-if="!isPlaying" aria-label="Lire la vidéo">
            <span class="play-icon"></span>
          </button>
        </transition>

        <!-- Controls bar shown on hover while playing -->
        <transition name="controls-fade">
          <div class="controls" v-if="isPlaying && hovered" @click.stop>
            <button class="ctrl-btn" @click="togglePlay" aria-label="Pause">
              <span class="pause-icon">
                <span></span>
                <span></span>
              </span>
            </button>
            <div class="progress-bar" @click="seekTo">
              <div class="progress-fill" :style="{ width: progress + '%' }"></div>
            </div>
            <div class="volume-group">
              <button class="ctrl-btn" @click="toggleMute" :aria-label="muted ? 'Activer le son' : 'Couper le son'">
                <span class="volume-icon" :class="{ muted }">
                  <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path v-if="!muted" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                    <path v-if="muted" d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                </span>
              </button>
              <input
                class="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                v-model.number="volume"
                @input="onVolumeChange"
                aria-label="Volume"
              />
            </div>
            <button class="ctrl-btn" @click="toggleFullscreen" :aria-label="isFullscreen ? 'Quitter le plein écran' : 'Plein écran'">
              <svg class="fullscreen-icon" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path v-if="!isFullscreen" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                <path v-if="isFullscreen" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
              </svg>
            </button>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FigaroVideo',
  data() {
    return {
      isPlaying: false,
      hovered: false,
      progress: 0,
      duration: 0,
      volume: 1,
      muted: false,
      isFullscreen: false,
    };
  },
  methods: {
    togglePlay() {
      const video = this.$refs.video;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },
    toggleMute() {
      const video = this.$refs.video;
      this.muted = !this.muted;
      video.muted = this.muted;
    },
    onVolumeChange() {
      const video = this.$refs.video;
      video.volume = this.volume;
      this.muted = this.volume === 0;
      video.muted = this.muted;
    },
    onTimeUpdate() {
      const video = this.$refs.video;
      if (video.duration) {
        this.progress = (video.currentTime / video.duration) * 100;
      }
    },
    onLoadedMetadata() {
      this.duration = this.$refs.video.duration;
    },
    seekTo(e) {
      const bar = e.currentTarget;
      const rect = bar.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      const video = this.$refs.video;
      video.currentTime = ratio * video.duration;
    },
    toggleFullscreen() {
      const wrapper = this.$refs.video;
      if (!document.fullscreenElement) {
        wrapper.requestFullscreen();
        this.isFullscreen = true;
      } else {
        document.exitFullscreen();
        this.isFullscreen = false;
      }
    },
  },
  mounted() {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
    });
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins.scss';

.figaro-video {
  // background-color: #d9d9d9;
  background: linear-gradient(180deg, #e0e0e0 0%, #d9d9d9 100%);
  padding: 80px 10%;
  color: black;
  margin-top: -20px;
  margin-bottom: 80px;
  .container {
    display: flex;
    align-items: center;
    gap: 60px;
  }

  .text-block {
    flex: 0 0 30%;
    max-width: 30%;

    h3 {
      color: black;
      margin-bottom: 20px;
    }

    .desc {
      color: black;
    }
    .figaro-link {
      margin-bottom: 0;
      a {
        font-size: 15px;
        display: flex;
        align-items: center;
        gap: 5px;
        &:hover {
          text-decoration: underline;
        }
        svg {
          width: 15px;
          height: 15px;
        }
      }
    }
  }

  .video-wrapper {
    flex: 1;
    position: relative;
    background: #000;

    &::before {
      content: '';
      display: block;
      padding-top: 56.25%;
    }

    video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      cursor: pointer;
    }

    &:hover {
      .play-btn {
        background-color: rgba(255, 255, 255, 0.3);
        transform: translate(-50%, -50%) scale(1.08);
      }
    }

    .play-btn {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.15);
      border: 2px solid rgba(255, 255, 255, 0.8);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.25s ease, transform 0.25s ease;
      padding: 0;

      .play-icon {
        display: block;
        width: 0;
        height: 0;
        margin-left: 5px;
        border-style: solid;
        border-width: 11px 0 11px 20px;
        border-color: transparent transparent transparent white;
      }
    }

    // ── Controls bar ──────────────────────────────────
    .controls {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 14px;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, transparent 100%);

      .ctrl-btn {
        flex-shrink: 0;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.85;
        transition: opacity 0.2s;

        &:hover {
          opacity: 1;
        }

        .pause-icon {
          display: flex;
          gap: 4px;
          span {
            display: block;
            width: 3px;
            height: 16px;
            background: white;
            border-radius: 1px;
          }
        }

        .volume-icon {
          display: flex;
          svg {
            width: 18px;
            height: 18px;
          }
        }

        .fullscreen-icon {
          width: 18px;
          height: 18px;
        }
      }

      .progress-bar {
        flex: 1;
        height: 4px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
        cursor: pointer;
        position: relative;
        transition: height 0.15s ease;

        &:hover {
          height: 6px;
        }

        .progress-fill {
          height: 100%;
          background: white;
          border-radius: 2px;
          pointer-events: none;
          transition: width 0.1s linear;
        }
      }

      .volume-group {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .volume-slider {
        -webkit-appearance: none;
        appearance: none;
        width: 70px;
        height: 3px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.35);
        cursor: pointer;
        outline: none;

        &::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
        }

        &::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: white;
          border: none;
          cursor: pointer;
        }
      }
    }
  }

  @include md {
    padding: 60px 10%;

    .container {
      flex-direction: column;
      gap: 40px;
    }

    .text-block {
      flex: none;
      max-width: 100%;
      width: 100%;
    }

    .video-wrapper {
      width: 100%;
    }
  }

  @include sm {
    padding: 50px 5%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.controls-fade-enter-active,
.controls-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.controls-fade-enter,
.controls-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>



// WEBPACK FOOTER //
// src/components/Modules/FigaroVideo.vue